import { v4 as uuidv4 } from 'uuid';
export function createGoal(data) {
    const now = new Date().toISOString();
    return {
        id: uuidv4(),
        title: data.title,
        description: data.description ?? null,
        // The goal form's defaults, so a goal made anywhere (the form, the
        // assistant) starts the same. These were 'yearly' / 'other' here while
        // the app built goals inline with 'quarterly' / 'personal'.
        time_horizon: data.time_horizon ?? 'quarterly',
        category: data.category ?? 'personal',
        target_date: data.target_date ?? null,
        linked_project_ids: data.linked_project_ids ?? [],
        linked_habit_ids: data.linked_habit_ids ?? [],
        linked_task_ids: data.linked_task_ids ?? [],
        parent_goal_id: data.parent_goal_id ?? null,
        status: 'active',
        progress: 0, // legacy field; real progress is derived
        progress_override: null,
        key_results: [],
        check_ins: [],
        created_at: now,
        updated_at: now,
        achieved_at: null,
    };
}
/**
 * Merge a patch into a goal, the way the app does.
 *
 * This lived inside `useGoals.updateGoal`, where only the React app could
 * reach it — the same place `applyProjectUpdate` started. The part that
 * matters is not the spread but the `achieved_at` rule below: a second
 * implementation would get the spread right and that rule wrong, and the
 * symptom is a goal marked achieved with no date on it, which nothing
 * surfaces until someone asks when they finished it.
 *
 * `now` is passed in rather than read from the clock, so callers on a server
 * and callers in a browser stamp the same way and tests are not time-
 * dependent.
 */
export function applyGoalUpdate(goal, update, now) {
    const next = { ...goal };
    for (const [key, value] of Object.entries(update)) {
        if (value === undefined)
            continue;
        // Exported from core, and the next caller may hand it parsed JSON
        // straight off the network — same guard as applyProjectUpdate.
        if (key === '__proto__' || key === 'constructor' || key === 'prototype')
            continue;
        next[key] = value;
    }
    /**
     * Stamp or clear the achievement date when status crosses that line, unless
     * the caller set it explicitly.
     *
     * `!goal.achieved_at` rather than an unconditional stamp: re-saving an
     * already-achieved goal must not move the date it was achieved on.
     */
    if (update.status !== undefined && update.achieved_at === undefined) {
        if (update.status === 'achieved' && !goal.achieved_at) {
            next.achieved_at = now;
        }
        else if (update.status !== 'achieved') {
            next.achieved_at = null;
        }
    }
    next.updated_at = now;
    return next;
}
export function goalDeletionEffect(goals, id, now) {
    const target = goals.find((g) => g.id === id);
    // The deleted goal's own parent — so a sub-goal moves UP one level rather
    // than to the top, which is what "the thing above it is gone" should mean.
    const newParent = target?.parent_goal_id ?? null;
    const reparented = goals
        .filter((g) => g.parent_goal_id === id)
        .map((child) => ({ ...child, parent_goal_id: newParent, updated_at: now }));
    return { removedId: id, reparented };
}
//# sourceMappingURL=goal.js.map